/** Authorized triage corrections are separate from initial photo submission. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { api, type Schemas } from "@/api/client";
import { errorMessage, unwrap } from "@/api/problem";
import { AssetPicker } from "@/features/assets/AssetPicker";
import { FormDialog } from "@/shared/components/form-dialog";
import { Field, Input, Select, Textarea } from "@/shared/ui/form";
import { REQUEST_CATEGORIES, REQUEST_SEVERITIES, type ServiceRequestDetail } from "./queries";

/** Preserve linked-job context while permitting title, diagnosis, severity and discipline corrections. */
export function RequestTriage({
  request,
  onClose,
}: {
  request: ServiceRequestDetail;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [title, setTitle] = useState(request.title);
  const [description, setDescription] = useState(request.description);
  const [assetId, setAssetId] = useState(request.asset_id ?? "");
  const [category, setCategory] = useState(request.category);
  const [severity, setSeverity] = useState(request.severity);
  const [discipline, setDiscipline] = useState(request.discipline);
  const save = useMutation({
    mutationFn: async (body: Schemas["RequestPatch"]) =>
      unwrap(
        await api.PATCH("/api/v1/requests/{request_id}", {
          params: { path: { request_id: request.id } },
          body,
        }),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["request", request.id] });
      void qc.invalidateQueries({ queryKey: ["requests"] });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      title={t("requests.editTriage")}
      onOpenChange={(open) => !open && onClose()}
      submitting={save.isPending}
      disabled={!title.trim()}
      error={save.isError ? errorMessage(save.error) : undefined}
      onSubmit={() =>
        save.mutateAsync({
          title,
          description,
          category,
          severity,
          discipline,
          ...(request.work_order_ids.length ? {} : { asset_id: assetId || null }),
        })
      }
    >
      <Field id="triage-title" label={t("app.title")}>
        <Input id="triage-title" value={title} onChange={(event) => setTitle(event.target.value)} />
      </Field>
      <Field id="triage-description" label={t("app.description")}>
        <Textarea
          id="triage-description"
          value={description}
          onChange={(event) => setDescription(event.target.value)}
        />
      </Field>
      {!request.work_order_ids.length ? (
        <Field id="triage-asset" label={t("app.asset")}>
          <AssetPicker id="triage-asset" value={assetId} onChange={setAssetId} required={false} />
        </Field>
      ) : null}
      <Field id="triage-category" label={t("app.type")}>
        <Select
          id="triage-category"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        >
          {REQUEST_CATEGORIES.map((value) => (
            <option key={value} value={value}>
              {t(`requests.category.${value}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="triage-severity" label={t("app.severity")}>
        <Select
          id="triage-severity"
          value={severity}
          onChange={(event) => setSeverity(event.target.value)}
        >
          {REQUEST_SEVERITIES.map((value) => (
            <option key={value} value={value}>
              {t(`requests.severity.${value}`)}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="triage-discipline" label={t("requests.disciplineLabel")}>
        <Select
          id="triage-discipline"
          value={discipline}
          onChange={(event) => setDiscipline(event.target.value)}
        >
          {["general", "mechanical", "electrical", "structural", "safety"].map((value) => (
            <option key={value} value={value}>
              {t(`requests.disciplines.${value}`)}
            </option>
          ))}
        </Select>
      </Field>
    </FormDialog>
  );
}
